// ---------------------------------------------------------
// Datei: driveUploadService.ts
// Zweck: Foto/Datei-Upload via Auth-API /google/drive/upload -> Google Drive
// Besonderheiten:
// - Nutzt Auth-API (localhost:7000) über Vite-Proxy (/auth-api)
// - Authentifizierung via Session-Cookie (credentials: include)
// - Sendet files + dealName + directories als FormData
// - Gibt UploadedFile[] mit fileId, fileName, webViewLink zurueck
// Stand: Maerz 2026
// ---------------------------------------------------------

import { getAuthApiBaseUrl, getGoogleAuthHeaders } from "./googleAuthSession";

const AUTH_BASE = getAuthApiBaseUrl();
const DRIVE_UPLOAD_URL = `${AUTH_BASE}/google/drive/upload`;
const DRIVE_UPLOAD_NEW_URL = `${AUTH_BASE}/google/drive/upload/new`;

export interface DriveUploadedFile {
  fileId: string;
  fileName: string;
  webViewLink: string;
}

export interface DriveUploadResponse {
  message: string;
  status: boolean;
  errors: string[];
  files: DriveUploadedFile[] | null;
  targetFolderId?: string;
  path?: string;
}

/**
 * Upload files to Google Drive via backend endpoint.
 *
 * @param files - Array of File objects to upload
 * @param dealName - HubSpot Deal name (e.g. "WAEP-123 Mueller")
 * @param directories - Subfolder path as array (e.g. ["Dokumente", "Allgemeine Uploads"])
 * @param onProgress - Optional callback for sequential upload progress
 */
export async function uploadFilesToDrive(
  files: File[],
  dealName: string,
  directories: string[] = ['Dokumente', 'Allgemeine Uploads'],
  sharedDrive?: string,
  gdriveFolderId?: string,
): Promise<DriveUploadResponse> {
  const authHeaders = await getGoogleAuthHeaders();

  // Neuer Endpoint wenn HubSpot-Folder-ID vorhanden – kein Fallback auf alten Endpoint,
  // da Upload bereits erfolgreich in Drive gelandet sein könnte (nicht idempotent).
  if (gdriveFolderId) {
    const newFormData = new FormData();
    for (const file of files) {
      newFormData.append('files', file);
    }
    newFormData.append('baseFolderId', gdriveFolderId);
    newFormData.append('directories', JSON.stringify(directories));

    const newResponse = await fetch(DRIVE_UPLOAD_NEW_URL, {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders,
      body: newFormData,
    });

    if (!newResponse.ok) {
      const text = await newResponse.text();
      throw new Error(`Upload fehlgeschlagen: ${newResponse.status} - ${text}`);
    }

    return newResponse.json();
  }

  // Alter Endpoint – nur wenn keine gdriveFolderId
  const formData = new FormData();
  for (const file of files) {
    formData.append('files', file);
  }
  formData.append('dealName', dealName);
  formData.append('directories', JSON.stringify(directories));
  if (sharedDrive) {
    formData.append('sharedDrive', sharedDrive);
  }

  const response = await fetch(DRIVE_UPLOAD_URL, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders,
    body: formData,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Upload fehlgeschlagen: ${response.status} - ${text}`);
  }

  return response.json();
}

/**
 * Helper: Convert a base64 dataURL to a File object.
 * Needed when photos are taken via camera (CaptureModal returns dataURL, not File).
 */
export function dataUrlToFile(dataUrl: string, fileName: string): File {
  const [meta, base64] = dataUrl.split(',');
  const mime = meta.match(/:(.*?);/)?.[1] || 'image/jpeg';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], fileName, { type: mime });
}
